module Client.Page.User.TalgilTriggerExecutionLog

open Browser.Types
open Client.Components
open Client.Domain
open Client.DomainTypes.Msg
open Client.Infrastructure.Api
open Elmish
open Fable.FontAwesome
open Fable.React
open Feliz
open Feliz.Bulma
open Fulma
open Shared.Dto
open Shared.Dto.Dto
open Shared.Dto.Page.User.TalgilTriggerExecutionLog
open Shared.Infrastructure

let private limit = 10

type DataModel = {
    Log: Talgil.TriggerExecutionDto list
    TriggerId: int
    IsRequestRunning: bool
    MoreDataAvailable: bool
    Session: UserSession
    TriggerNames: TriggerNamesDto
}

type LoadingModel = {
    Session: UserSession
    TriggerId: int
    TriggerNames: TriggerNamesDto option
    Log: Talgil.TriggerExecutionDto list option
}

type Model = Loadable<DataModel, LoadingModel>

let private appendNewData (data: Talgil.TriggerExecutionDto list) (model: DataModel) =
    let moreDataAvailable = data.Length >= limit

    {
        model with
            Log = model.Log @ data
            MoreDataAvailable = moreDataAvailable
    }

let private tryCreateDataModel (loadingModel: LoadingModel) =
    match loadingModel.TriggerNames, loadingModel.Log with
    | Some names, Some log ->
        {
            Session = loadingModel.Session
            TriggerId = loadingModel.TriggerId
            IsRequestRunning = false
            MoreDataAvailable = false
            Log = []
            TriggerNames = names
        }
        |> appendNewData log
        |> Loadable.Data
    | _ -> Loadable.Loading loadingModel

let init (triggerId: int) (session: UserSession) =
    let request: AuthenticatedRequest<DataRequest> = {
        SessionKey = session.SessionKey
        Data = {
            TriggerId = triggerId
            Pagination = {
                Offset = 0
                Limit = limit
            }
        }
    }

    let logCmd =
        Cmd.OfAsync.perform api.getTalgilTriggerExecutionLog request (DataReceived >> TalgilTriggerExecutionLog)

    let namesCmd =
        Cmd.OfAsync.perform
            api.getTalgilTriggerNames
            {
                SessionKey = session.SessionKey
                Data = triggerId
            }
            (NamesReceived >> TalgilTriggerExecutionLog)

    Loadable.Loading {
        Session = session
        TriggerId = triggerId
        TriggerNames = None
        Log = None
    },
    Cmd.batch [ logCmd; namesCmd ]

let private requestFinished (model: DataModel) = { model with IsRequestRunning = false }

let update (msg: TalgilTriggerExecutionLogMsg) (model: Model) =
    match msg, model with
    | DataReceived response, Loadable.Loading loadingModel ->
        match response with
        | Result.Ok data ->
            let newModel = { loadingModel with Log = Some data } |> tryCreateDataModel

            newModel, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | NamesReceived response, Loadable.Loading loadingModel ->
        match response with
        | Result.Ok names ->
            let newModel = { loadingModel with TriggerNames = Some names } |> tryCreateDataModel

            newModel, Cmd.none
        | Result.Error _ -> Loadable.Error ErrorConfirmation.defaultErrorMessage, Cmd.none
    | RequestData pagination, Loadable.Data dataModel ->
        let request = {
            SessionKey = dataModel.Session.SessionKey
            Data = {
                TriggerId = dataModel.TriggerId
                Pagination = pagination
            }
        }

        let cmd =
            Cmd.OfAsync.perform api.getTalgilTriggerExecutionLog request (DataReceived >> TalgilTriggerExecutionLog)

        Loadable.Data { dataModel with IsRequestRunning = true }, cmd
    | DataReceived response, Loadable.Data dataModel ->
        match response with
        | Result.Ok data ->
            let newDataModel = appendNewData data dataModel |> requestFinished

            Loadable.Data newDataModel, Cmd.none
        | Result.Error err ->
            let cmd = ErrorConfirmation.responseError ErrorConfirmation.defaultConfig err

            Loadable.Data { dataModel with IsRequestRunning = false }, cmd
    | _, _ -> model, Cmd.none


let private onLoadMoreClick dispatch (model: DataModel) (_: MouseEvent) =
    TalgilTriggerExecutionLogMsg.RequestData {
        Offset = List.length model.Log
        Limit = limit
    }
    |> TalgilTriggerExecutionLog
    |> dispatch

let private createLoadMoreButton dispatch (model: DataModel) =
    let f = onLoadMoreClick dispatch model

    Pagination.createLoadMoreButton model.IsRequestRunning "10 weitere Einträge laden" f

let private executionSource (source: Talgil.SourceDto) : ReactElement * string =
    match source with
    | Talgil.SensorData data ->
        Icon.icon [ Icon.Size IsSmall ] [ Fa.i [ Fa.Solid.Laptop ] [] ],
        "Sensor-Daten: " + (MeasuredValueString.percentageToString data.SoilMoisture)
    | Talgil.Manual -> Icon.icon [ Icon.Size IsSmall ] [ Fa.i [ Fa.Solid.HandPointer ] [] ], "Manuell"


let private executionsView (index: int) (execution: Talgil.TriggerExecutionDto) =
    let elapsedTime =
        execution.ExecutionTime
        |> TimeSpan.createElapsedTime
        |> TimeSpan.toHumanReadable
        |> sprintf "vor %s"

    let receivedTime =
        execution.ExecutionTime
        |> DateTimeOffset.toLocalDateTime
        |> DateTime.toShortString

    let borderClass, iconClass =
        match execution.ExecutionResult.Type with
        | Talgil.Success -> "success-box", "fa-check-circle"
        | Talgil.Info -> "info-box", "fa-info-circle"
        | Talgil.Error -> "error-box", "fa-times-circle"

    let sourceIcon, sourceText = executionSource execution.TriggerSource

    Bulma.box [
        prop.className borderClass

        prop.children [
            Bulma.title [
                title.is3
                prop.text (sprintf "Eintrag #%i" (index + 1))
            ]

            Bulma.table [
                table.isFullWidth

                prop.children [
                    Html.thead [
                        prop.children [
                            Html.tr [
                                prop.children [
                                    Html.td [
                                        prop.children [
                                            Html.div [
                                                prop.className "with-icon-left"

                                                prop.children [
                                                    Icon.icon [ Icon.Size IsSmall ] [ Fa.i [ Fa.Solid.Clock ] [] ]

                                                    span [] [
                                                        str (sprintf "%s (%s)" receivedTime elapsedTime)
                                                    ]
                                                ]
                                            ]
                                        ]
                                    ]
                                ]
                            ]
                            Html.tr [
                                prop.children [
                                    Html.td [
                                        prop.className "with-icon-left"

                                        prop.children [
                                            sourceIcon

                                            Html.span [ prop.text sourceText ]
                                        ]
                                    ]
                                ]
                            ]

                            Html.tr [
                                prop.children [
                                    Html.td [
                                        prop.children [
                                            Html.div [
                                                prop.className "with-icon-left"

                                                prop.children [
                                                    Bulma.icon [
                                                        icon.isSmall

                                                        prop.children [ Html.i [ prop.classes [ "fas"; iconClass ] ] ]
                                                    ]

                                                    Html.span [ prop.text execution.ExecutionResult.Message ]
                                                ]
                                            ]
                                        ]
                                    ]
                                ]
                            ]
                        ]
                    ]
                ]
            ]
        ]
    ]

let private triggerView (trigger: TriggerNamesDto) =
    Bulma.level [
        Bulma.levelItem [
            prop.className "flex-level-item"

            prop.children [
                Html.span [ prop.text "Controller:" ]
                Html.span [
                    prop.style [ style.fontWeight 700 ]
                    prop.text trigger.ControllerName
                ]
            ]
        ]
        Bulma.levelItem [
            prop.className "flex-level-item"

            prop.children [
                Html.span [ prop.text "Programm:" ]
                Html.span [
                    prop.style [ style.fontWeight 700 ]
                    prop.text trigger.ProgramName
                ]
            ]
        ]
        Bulma.levelItem [
            prop.className "flex-level-item"

            prop.children [
                Html.span [ prop.text "Sensor:" ]
                Html.span [
                    prop.style [ style.fontWeight 700 ]
                    prop.text trigger.Sensor
                ]
            ]
        ]
    ]

let private listView (log: Talgil.TriggerExecutionDto list) =
    match log with
    | [] -> [
        Heading.h1 [] [
            str "Es wurden keine Einträge zu diesem Talgil Trigger gefunden."
        ]
      ]
    | _ -> List.mapi executionsView log

let private heading (name: string) =
    Heading.h1 [ Heading.CustomClass "has-text-centered" ] [ str name ]

let dataView dispatch (model: DataModel) =
    Bulma.container [
        container.isFullHd
        prop.classes [ "flex-container" ]

        prop.children [
            heading "Talgil Trigger Log"

            triggerView model.TriggerNames

            yield! listView model.Log

            if model.MoreDataAvailable then
                createLoadMoreButton dispatch model
            else
                ()
        ]
    ]


let view dispatch (model: Model) = Loadable.view (dataView dispatch) model